<script setup lang="ts">
  import { ref, watch } from 'vue'
  import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle
  } from '@/components/ui/dialog'
  import { Button } from '@/components/ui/button'
  import { X } from 'lucide-vue-next'

  interface Props {
    open: boolean
    title?: string
    description?: string
    confirmText?: string
    cancelText?: string
  }

  const props = withDefaults(defineProps<Props>(), {
    title: 'Changes not saved',
    description: 'Are you sure you want to leave without saving your changes?',
    confirmText: 'Discard changes',
    cancelText: 'Go back'
  })

  const emit = defineEmits<{
    'update:open': [value: boolean]
    confirm: []
    cancel: []
  }>()

  const isOpen = ref(props.open)

  watch(
    () => props.open,
    (newValue) => {
      isOpen.value = newValue
    }
  )

  const handleOpenChange = (open: boolean) => {
    isOpen.value = open
    emit('update:open', open)
  }

  const handleConfirm = () => {
    emit('confirm')
    handleOpenChange(false)
  }

  const handleCancel = () => {
    emit('cancel')
    handleOpenChange(false)
  }

  const handleClose = () => {
    emit('cancel')
    handleOpenChange(false)
  }
</script>

<template>
  <Dialog
    :open="isOpen"
    @update:open="handleOpenChange"
  >
    <DialogContent class="p-6 sm:max-w-md">
      <!-- Close button -->
      <button
        class="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity
          hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
        @click="handleClose"
      >
        <X class="h-4 w-4" />
        <span class="sr-only">Close</span>
      </button>

      <!-- Header -->
      <DialogHeader class="pb-4 text-center">
        <DialogTitle class="text-xl font-semibold text-gray-900">
          {{ title }}
        </DialogTitle>
      </DialogHeader>

      <!-- Description -->
      <DialogDescription class="mb-6 text-center text-gray-600">
        {{ description }}
      </DialogDescription>

      <!-- Footer with buttons -->
      <DialogFooter class="flex flex-col-reverse gap-3 sm:flex-row sm:justify-center">
        <Button
          variant="secondary"
          class="w-full px-6 py-2 sm:w-auto"
          @click="handleCancel"
        >
          {{ cancelText }}
        </Button>
        <Button
          class="w-full bg-purple-600 px-6 py-2 text-white hover:bg-purple-700 sm:w-auto"
          @click="handleConfirm"
        >
          {{ confirmText }}
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
</template>
